import { EditableTablePreviewResponseV2 } from "@/apis/editable-preview/types";
import {
  getCellElementSafely,
  getWorksheetInstance,
} from "@/jspreadsheet/jspreadsheet";
import queries from "@/queries";
import { useSpreadsheetStore } from "@/stores/spreadsheet";
import { getCellsFromRange } from "@/utils/cell";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

const PREVIEW_ISSUE_CELL_CLASS = "preview-issue-cell";
const PREVIEW_ISSUE_PREVIEW_CACHE_TIME = 1000 * 60 * 5;

const normalizeCellAddress = (address: string) => {
  const [, cellAddress = address] = address.split("!");
  return cellAddress.trim();
};

const expandCellAddress = (address: string) => {
  try {
    return getCellsFromRange(normalizeCellAddress(address));
  } catch {
    return [];
  }
};

const collectPreviewIssueCellAddresses = (
  tablePreview: EditableTablePreviewResponseV2 | null | undefined,
) => {
  const issueCellAddresses = new Set<string>();

  tablePreview?.omittedValues?.forEach((omittedValue) => {
    expandCellAddress(omittedValue.cell.cellAddress).forEach((cellAddress) =>
      issueCellAddresses.add(cellAddress),
    );
  });

  tablePreview?.duplicatedValues?.forEach((duplicatedValue) => {
    duplicatedValue.values
      .filter((value) => !value.isVisible)
      .forEach((value) => {
        expandCellAddress(value.cellAddress).forEach((cellAddress) =>
          issueCellAddresses.add(cellAddress),
        );
      });
  });

  return Array.from(issueCellAddresses).sort();
};

const getCellHighlightElement = (cell: HTMLElement): HTMLElement =>
  cell.closest("td") ?? cell;

const removePreviewIssueCellClass = (cell: HTMLElement) => {
  cell.classList.remove(PREVIEW_ISSUE_CELL_CLASS);
};

export const usePreviewIssueCellHighlight = ({
  workbookId,
  tableId,
  enabled,
}: {
  workbookId?: string | null;
  tableId?: string | null;
  enabled: boolean;
}) => {
  const queryEnabled = enabled && !!workbookId && !!tableId;

  // worksheet 마운트 시점 신호. 새로고침 시 worksheet가 데이터보다 늦게 마운트되면
  // effect를 재실행해 하이라이트를 다시 적용한다.
  const activeSheet = useSpreadsheetStore((state) => state.activeSheet);

  const { data: notUsedSumTagKeysData } = useQuery({
    ...queries
      .workbook(workbookId ?? "")
      ._ctx.tables(tableId ?? "")
      ._ctx.notUsedSumTagKeys(),
    enabled: queryEnabled,
  });

  const { data: tablePreview } = useQuery({
    ...queries
      .workbook(workbookId ?? "")
      ._ctx.tables(tableId ?? "")
      ._ctx.editablePreview()
      ._ctx.withQueryParam(notUsedSumTagKeysData?.notUsedSumTagKeys ?? []),
    enabled: queryEnabled && !!notUsedSumTagKeysData,
    gcTime: PREVIEW_ISSUE_PREVIEW_CACHE_TIME,
    staleTime: PREVIEW_ISSUE_PREVIEW_CACHE_TIME,
  });

  const issueCellAddresses = useMemo(
    () => collectPreviewIssueCellAddresses(tablePreview),
    [tablePreview],
  );

  useEffect(() => {
    if (!queryEnabled || issueCellAddresses.length === 0) return;

    const worksheet = getWorksheetInstance();
    if (!worksheet) return;
    const worksheetTableId = worksheet.options.meta?.info?.tableId;
    if (worksheetTableId && worksheetTableId !== tableId) return;

    // 이 effect 실행이 실제로 변경한 DOM 요소를 직접 소유한다. cleanup 시점의
    // 활성 worksheet가 바뀌거나 사라져도 정확히 같은 요소에서 클래스를 제거한다.
    const highlightedCells = new Set<HTMLElement>();

    issueCellAddresses.forEach((address) => {
      const cell = getCellElementSafely(worksheet, address);
      if (!cell) return;

      const highlightedCell = getCellHighlightElement(cell);
      highlightedCell.classList.add(PREVIEW_ISSUE_CELL_CLASS);
      highlightedCells.add(highlightedCell);
    });

    return () => {
      highlightedCells.forEach(removePreviewIssueCellClass);
    };
  }, [issueCellAddresses, queryEnabled, activeSheet, tableId]);
};
